import { Link, useRouteError, isRouteErrorResponse } from "react-router";
import {
  captureRemixErrorBoundaryError,
  captureException,
} from "@sentry/remix";
import { Document } from "./Document";
import { Button } from "~/components/button/Button";

export function ErrorBoundary({
  showDetails = false,
}: { showDetails?: boolean } = {}) {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);

  const isDev = process.env.NODE_ENV === "development";

  if (isRouteErrorResponse(error)) {
    return (
      <Document title={error.statusText}>
        <ErrorDisplay
          title={`${error.status} ${error.statusText}`}
          message={
            error.data?.message || "Something went wrong with your request."
          }
          statusCode={error.status}
          showDetails={showDetails}
          errorDetails={
            (showDetails || isDev) && error.data
              ? JSON.stringify(error.data, null, 2)
              : undefined
          }
          fullError={isDev ? error : undefined}
        />
      </Document>
    );
  }

  const unexpectedError = error as Error;
  if (unexpectedError) captureException(unexpectedError);

  return (
    <Document title="Unexpected Error">
      <ErrorDisplay
        title="Unexpected Error"
        message={
          isDev
            ? `${unexpectedError.name}: ${unexpectedError.message}`
            : "Sorry, something went wrong. We've been notified and are looking into it."
        }
        showDetails={showDetails}
        errorDetails={
          (showDetails || isDev) && unexpectedError?.stack
            ? unexpectedError.stack
            : undefined
        }
        fullError={isDev ? unexpectedError : undefined}
      />
    </Document>
  );
}

interface ErrorDisplayProps {
  title: string;
  message: string;
  statusCode?: number;
  showDetails?: boolean;
  errorDetails?: string | null;
  fullError?: Error | object | null;
}

function ErrorDisplay({
  title,
  message,
  statusCode,
  showDetails,
  errorDetails,
  fullError,
}: ErrorDisplayProps) {
  const isDevelopment = process.env.NODE_ENV === "development";
  const shouldShowDetails = showDetails || isDevelopment;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-ms-gray-50 p-6">
      <div className="max-w-4xl text-center">
        {/* Only shown in dev, lets devs know extra info is visible */}
        {isDevelopment && (
          <div className="mb-6 rounded-md border border-yellow-400 bg-yellow-100 p-3 text-sm text-yellow-800">
            🚧 <strong>Development Mode:</strong> Detailed error information
            shown below
          </div>
        )}

        {/* Icon */}
        <div className="mb-6">
          <svg
            className="mx-auto h-16 w-16 text-ms-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
        </div>

        {/* Error title + message */}
        <h1 className="mb-2 text-3xl font-semibold text-ms-gray-900">
          {title}
        </h1>
        <p className="mb-8 text-lg text-ms-gray-600">
          {message}
        </p>

        {/* HTTP status (if available) */}
        {statusCode && (
          <p className="mb-6 font-mono text-sm text-ms-gray-500">
            Error Code: {statusCode}
          </p>
        )}

        <div className="space-y-4">
          {/* Recovery actions */}
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            {/* Client-only: must guard with typeof window to avoid server crash */}
            <Button
              variant="outline"
              size="md"
              onClick={() =>
                typeof window !== "undefined" && window.location.reload()
              }
            >
              Try Again
            </Button>

            <Button variant="primary" size="md" asChild>
              <Link to="/">Go Home</Link>
            </Button>

            <Button
              variant="secondary"
              size="md"
              onClick={() =>
                typeof window !== "undefined" && window.history.back()
              }
            >
              Go Back
            </Button>
          </div>

          {/* Detailed error/debug info, dev only */}
          {shouldShowDetails && (
            <div className="mt-8 space-y-4">
              {/* Stack trace */}
              {errorDetails && (
                <details className="text-left" open={isDevelopment}>
                  <summary className="cursor-pointer text-sm font-medium text-ms-gray-700 transition-colors hover:text-ms-gray-900">
                    {isDevelopment ? "Stack Trace" : "Show Error Details"}
                  </summary>
                  <div className="mt-4 rounded-md bg-ms-gray-100 p-4">
                    <pre className="overflow-auto whitespace-pre-wrap font-mono text-xs text-ms-gray-800">
                      {errorDetails}
                    </pre>
                  </div>
                </details>
              )}

              {/* Full error object */}
              {isDevelopment && fullError && (
                <details className="text-left">
                  <summary className="cursor-pointer text-sm font-medium text-red-700 transition-colors hover:text-red-900">
                    Full Error Object (Dev Only)
                  </summary>
                  <div className="mt-4 rounded-md bg-red-50 p-4">
                    <pre className="overflow-auto whitespace-pre-wrap font-mono text-xs text-red-800">
                      {(() => {
                        try {
                          if (fullError instanceof Error) {
                            const errorObj: Record<string, unknown> = {
                              name: fullError.name,
                              message: fullError.message,
                              stack: fullError.stack,
                              ...Object.getOwnPropertyNames(fullError).reduce(
                                (acc: Record<string, unknown>, prop) => {
                                  acc[prop] = (
                                    fullError as unknown as Record<
                                      string,
                                      unknown
                                    >
                                  )[prop];
                                  return acc;
                                },
                                {},
                              ),
                            };
                            return JSON.stringify(errorObj, null, 2);
                          } else {
                            return JSON.stringify(fullError, null, 2);
                          }
                        } catch {
                          return `Error serializing: ${String(fullError)}`;
                        }
                      })()}
                    </pre>
                  </div>
                </details>
              )}

              {/* Browser-only environment info. Guarded to avoid ReferenceError on server */}
              {isDevelopment && typeof window !== "undefined" && (
                <details className="text-left">
                  <summary className="cursor-pointer text-sm font-medium text-blue-700 transition-colors hover:text-blue-900">
                    Environment Info (Dev Only)
                  </summary>
                  <div className="mt-4 rounded-md bg-blue-50 p-4">
                    <div className="space-y-2 text-xs text-blue-800">
                      <div>
                        <strong>URL:</strong> {window.location.href}
                      </div>
                      <div>
                        <strong>User Agent:</strong> {navigator.userAgent}
                      </div>
                      <div>
                        <strong>Timestamp:</strong> {new Date().toISOString()}
                      </div>
                      <div>
                        <strong>Referrer:</strong>{" "}
                        {document.referrer || "Direct"}
                      </div>
                    </div>
                  </div>
                </details>
              )}
            </div>
          )}

          {/* Footer help text */}
          <div className="mt-8 border-t border-ms-gray-200 pt-8">
            <p className="text-sm text-ms-gray-500">
              {isDevelopment ? (
                "💡 This detailed error info is only shown in development mode."
              ) : (
                <>If this problem persists, please contact support.</>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
